import { useState } from "react";
import { useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cellsPerSheet, printLabels } from "./labelSheet";
import {
  findByCode,
  modelOf,
  type RacketOverview,
  type Variant,
} from "./racketData";
import { useLabelFormat } from "./racketHooks";
import { ScanField } from "./ScanField";

// Several labels on the same A4 sheet (task 1.11, D-041): the rackets are
// ticked by hand or shot with the reader; the starting cell skips the cells
// already used on a sheet begun earlier.
export const PrintLabelsDialog = ({
  rackets,
  variants,
  onClose,
}: {
  rackets: RacketOverview[];
  variants: Variant[];
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const format = useLabelFormat();
  const cells = cellsPerSheet(format);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [startCell, setStartCell] = useState("1");
  const start = Number(startCell);
  const isStartValid = Number.isInteger(start) && start >= 1 && start <= cells;

  const toggle = (id: number, isOn: boolean) =>
    setPicked((current) => {
      const next = new Set(current);
      if (isOn) next.add(id);
      else next.delete(id);
      return next;
    });
  const onCode = (code: string) => {
    const racket = findByCode(rackets, code);
    if (racket) toggle(racket.id, true);
    else
      notify("tenax.rackets.unknown_code", {
        type: "warning",
        messageArgs: { code },
      });
  };
  const chosen = rackets.filter((racket) => picked.has(racket.id));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          document.getElementById("labels-scan")?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{translate("tenax.rackets.labels.title")}</DialogTitle>
          <DialogDescription>
            {translate("tenax.rackets.labels.hint", {
              columns: format.columns,
              rows: format.rows,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <ScanField
            id="labels-scan"
            label={translate("tenax.rackets.scan")}
            onCode={onCode}
          />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="labels-start">
              {translate("tenax.rackets.labels.start_cell", { cells })}
            </Label>
            <Input
              id="labels-start"
              type="number"
              min={1}
              max={cells}
              value={startCell}
              onChange={(event) => setStartCell(event.target.value)}
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">
              {translate("tenax.rackets.labels.chosen", {
                smart_count: chosen.length,
              })}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                setPicked(
                  chosen.length === rackets.length
                    ? new Set()
                    : new Set(rackets.map((racket) => racket.id)),
                )
              }
            >
              {translate(
                chosen.length === rackets.length
                  ? "tenax.rackets.labels.none"
                  : "tenax.rackets.labels.all",
              )}
            </Button>
          </div>
          <ul
            aria-label={translate("tenax.rackets.labels.list")}
            className="flex max-h-64 flex-col gap-1 overflow-y-auto"
          >
            {rackets.map((racket) => (
              <li key={racket.id}>
                <label className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm">
                  <Checkbox
                    checked={picked.has(racket.id)}
                    onCheckedChange={(value) =>
                      toggle(racket.id, value === true)
                    }
                  />
                  <span className="font-mono">{racket.code}</span>
                  <span className="truncate">{modelOf(racket, variants)}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            type="button"
            disabled={!chosen.length || !isStartValid}
            onClick={() =>
              printLabels(
                chosen.map((racket) => ({
                  code: racket.code,
                  model: modelOf(racket, variants),
                })),
                format,
                start,
              )
            }
          >
            {translate("tenax.rackets.labels.print", {
              smart_count: chosen.length,
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
